import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  BiCalendar,
  BiChevronLeft,
  BiChevronRight,
  BiListCheck,
  BiMoney,
  BiSolidXCircle,
  BiTime,
} from 'react-icons/bi';
import type { IconType } from 'react-icons';
import { useAuth } from '@/auth/AuthContext';
import { Banner } from '@/components/common/Banner';
import { getCalendar } from '@/api/calendar.api';
import { CalendarGrid } from '@/pages/calendar/CalendarGrid';
import { CalendarAgenda } from '@/pages/calendar/CalendarAgenda';
import {
  bucketEventsByDate,
  cairoDateString,
  computeMonthGridWindow,
  isValidMonthKey,
  monthKeyFromDateString,
  shiftMonthKey,
} from '@/lib/calendarDate';
import { formatMonthYear, intlLocale } from '@/lib/format';
import { CALENDAR_EVENT_TYPES_BY_ROLE } from '@/types/shared';
import type { CalendarEventType, CalendarResponse } from '@/types/shared';
import './CalendarPage.css';

type FetchStatus = 'loading' | 'loaded' | 'error';

const LEGEND_ICON: Record<CalendarEventType, IconType> = {
  exam: BiListCheck,
  meeting: BiTime,
  payment: BiMoney,
};

const LEGEND_LABEL_KEY: Record<CalendarEventType, string> = {
  exam: 'calendar.legend.exams',
  meeting: 'calendar.legend.meetings',
  payment: 'calendar.legend.payments',
};

const SKELETON_WEEK_COUNT = 5;

/**
 * `/calendar` — T107, built to the frozen T103 contract (`GET /calendar?from=&to=`) and the T104
 * UX spec. Owns: the visible-month URL state (`?month=YYYY-MM`, T104 §1.5b), the fetch/loading/
 * empty/error state machine, month nav + live-region announcements, and delegates rendering to
 * `CalendarGrid` (desktop/tablet, `≥ bp.md`) and `CalendarAgenda` (mobile, `< bp.md`) — both
 * driven by the same `computeMonthGridWindow`/`bucketEventsByDate` data so they can never disagree.
 */
export function CalendarPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const language = i18n.language;
  const [searchParams, setSearchParams] = useSearchParams();

  const todayStr = cairoDateString();
  const rawMonth = searchParams.get('month');
  const monthKey = isValidMonthKey(rawMonth) ? rawMonth : monthKeyFromDateString(todayStr);

  // A missing/malformed `?month=` is corrected in place (replace, not a new history entry) —
  // every subsequent Prev/Next/Today nav pushes a real, back-button-able entry (T104 §1.5b).
  useEffect(() => {
    if (!isValidMonthKey(rawMonth)) {
      setSearchParams({ month: monthKey }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawMonth]);

  const gridWindow = useMemo(() => computeMonthGridWindow(monthKey), [monthKey]);
  const days = useMemo(() => gridWindow.weeks.flat(), [gridWindow]);

  const [status, setStatus] = useState<FetchStatus>('loading');
  const [response, setResponse] = useState<CalendarResponse | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const headingRef = useRef<HTMLHeadingElement>(null);
  const retryButtonRef = useRef<HTMLButtonElement>(null);
  const userTriggeredRetryRef = useRef(false);
  const prevStatusRef = useRef<FetchStatus>('loading');
  const isInitialMountRef = useRef(true);
  const [liveMessage, setLiveMessage] = useState('');

  // Initial page load only (T104 §1.1 step 2 / §7.2) — never on subsequent month navigation.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    getCalendar({ from: gridWindow.from, to: gridWindow.to })
      .then((data) => {
        if (cancelled) return;
        setResponse(data);
        setStatus('loaded');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [gridWindow.from, gridWindow.to, reloadToken]);

  // Retry-focus discipline (T104 §5.3): ANY transition into `error` moves focus to the retry
  // button (first-load failure included, not just after an explicit retry click); a *user-
  // triggered* retry that then succeeds returns focus to the page heading (mirrors
  // `useWidgetRetryFocus`'s "always return focus somewhere predictable" rule, adapted here since a
  // first-load error also needs the retry button focused, which that shared hook doesn't do).
  useEffect(() => {
    const wasLoading = prevStatusRef.current === 'loading';
    prevStatusRef.current = status;

    if (status === 'error') {
      retryButtonRef.current?.focus();
      return;
    }

    if (wasLoading && status === 'loaded' && userTriggeredRetryRef.current) {
      userTriggeredRetryRef.current = false;
      headingRef.current?.focus();
    }
  }, [status]);

  // Live-region month announcement (T104 §1.3) — only on navigation, not the very first mount
  // (the initial-load heading focus already gives that signal, §7.2).
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    const locale = intlLocale(language);
    const date = new Date(`${monthKey}-01T00:00:00Z`);
    const month = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }).format(date);
    const year = new Intl.DateTimeFormat(locale, { year: 'numeric', timeZone: 'UTC' }).format(date);
    setLiveMessage(t('calendar.monthAnnouncementTemplate', { month, year }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthKey, language]);

  function handlePrev() {
    setSearchParams({ month: shiftMonthKey(monthKey, -1) });
  }

  function handleNext() {
    setSearchParams({ month: shiftMonthKey(monthKey, 1) });
  }

  function handleToday() {
    setSearchParams({ month: monthKeyFromDateString(todayStr) });
  }

  function handleRetry() {
    userTriggeredRetryRef.current = true;
    setReloadToken((v) => v + 1);
  }

  const eventsByDate = useMemo(() => bucketEventsByDate(response?.events ?? []), [response]);

  const isTodayMonth = monthKey === monthKeyFromDateString(todayStr);
  const isEmpty = status === 'loaded' && (response?.events.length ?? 0) === 0;
  const allowedTypes = user ? CALENDAR_EVENT_TYPES_BY_ROLE[user.role] : [];
  const monthYearLabel = formatMonthYear(monthKey, language);
  const captionText = t('calendar.captionTemplate', { monthYear: monthYearLabel });

  return (
    <section className="fa-calendar">
      <h1 ref={headingRef} tabIndex={-1} className="fa-calendar__title">
        {t('calendar.title')}
      </h1>

      <div className="fa-visually-hidden" aria-live="polite" aria-atomic="true">
        {liveMessage}
      </div>

      <div className="fa-calendar__nav">
        <button
          type="button"
          className="fa-calendar__nav-btn"
          onClick={handlePrev}
          aria-label={t('calendar.nav.previousMonth')}
        >
          <BiChevronLeft className="fa-calendar__nav-icon" aria-hidden="true" />
        </button>

        <span className="fa-calendar__month-label">{monthYearLabel}</span>

        <button
          type="button"
          className="fa-calendar__nav-btn"
          onClick={handleNext}
          aria-label={t('calendar.nav.nextMonth')}
        >
          <BiChevronRight className="fa-calendar__nav-icon" aria-hidden="true" />
        </button>

        <button
          type="button"
          className="fa-btn fa-btn--secondary fa-calendar__today-btn"
          onClick={handleToday}
          disabled={isTodayMonth}
        >
          {t('calendar.nav.today')}
        </button>
      </div>

      {allowedTypes.length > 0 && (
        <ul className="fa-calendar__legend">
          {allowedTypes.map((type) => {
            const Icon = LEGEND_ICON[type];
            return (
              <li
                key={type}
                className={`fa-calendar__legend-item fa-calendar__legend-item--${type}`}
              >
                <Icon aria-hidden="true" />
                <span>{t(LEGEND_LABEL_KEY[type])}</span>
              </li>
            );
          })}
          {/* 2026-08 — overdue installments render red rather than the teal payment colour, so the
              legend has to explain that state; shown only to roles that actually get payment events
              (an Exam Coordinator would have nothing to apply it to). This is a STATUS entry, not a
              fourth event type — deliberately not added to CALENDAR_EVENT_TYPES, which is a closed
              union the backend dispatches on. */}
          {allowedTypes.includes('payment') && (
            <li className="fa-calendar__legend-item fa-calendar__legend-item--payment-overdue">
              <BiSolidXCircle aria-hidden="true" />
              <span>{t('calendar.legend.overdue')}</span>
            </li>
          )}
        </ul>
      )}

      {status === 'error' && (
        <Banner
          tone="info"
          action={
            <button
              type="button"
              ref={retryButtonRef}
              className="fa-btn fa-btn--secondary"
              onClick={handleRetry}
            >
              {t('calendar.states.retryLabel')}
            </button>
          }
        >
          {t('calendar.states.errorMessage')}
        </Banner>
      )}

      {status !== 'error' && (
        <div
          className="fa-calendar__body"
          aria-busy={status === 'loading'}
          aria-label={status === 'loading' ? t('calendar.loadingAriaLabel') : undefined}
        >
          {status === 'loading' ? (
            <CalendarGridSkeleton />
          ) : (
            <>
              <CalendarGrid
                weeks={gridWindow.weeks}
                eventsByDate={eventsByDate}
                monthKey={monthKey}
                todayStr={todayStr}
                language={language}
                captionText={captionText}
              />
              <CalendarAgenda
                days={days}
                eventsByDate={eventsByDate}
                todayStr={todayStr}
                language={language}
                isEmpty={isEmpty}
              />
            </>
          )}
        </div>
      )}

      {isEmpty && (
        <p className="fa-calendar__empty-note">
          <BiCalendar aria-hidden="true" />
          {t('calendar.states.empty')}
        </p>
      )}
    </section>
  );
}

/** T104 §5.1 — shimmering placeholder cells, same 7-column structure, `aria-hidden` (the wrapping
 * `.fa-calendar__body` above already carries `aria-busy` + the visually-hidden busy-region label,
 * mirroring `ActivityFeedWidget`'s exact skeleton pattern). */
function CalendarGridSkeleton() {
  return (
    <div className="fa-calendar__skeleton" aria-hidden="true">
      {Array.from({ length: SKELETON_WEEK_COUNT }).map((_, weekIdx) => (
        <div key={weekIdx} className="fa-calendar__skeleton-row">
          {Array.from({ length: 7 }).map((_, dayIdx) => (
            <div key={dayIdx} className="fa-calendar__skeleton-cell">
              <span className="fa-calendar__skeleton-date" />
              <span className="fa-calendar__skeleton-chip" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
